import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, useTheme } from '../App';
import { PageLayout, useSidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, addDays, subDays, isToday, isBefore, isAfter, parseISO } from 'date-fns';
import {
  Plus, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, Moon,
  RefreshCw, AlertTriangle, GripHorizontal, ListTodo, Inbox, Settings,
  CheckCircle2, FileText, X, Check, Loader2, Eye, Users
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useOrg } from '../contexts/OrgContext';
import { useWorkAreas } from '../contexts/WorkAreaContext';
import Timeline from '../components/dashboard/Timeline';
import ScheduledPane from '../components/dashboard/ScheduledPane';
import BacklogPane from '../components/dashboard/BacklogPane';
import TaskModal from '../components/dashboard/TaskModal';
import AssistantPanel from '../components/dashboard/AssistantPanel';
import EODModal from '../components/dashboard/EODModal';
import DatePickerWithStats from '../components/dashboard/DatePickerWithStats';
import { TodayCommandBand } from '../components/orka/dashboard/TodayCommandBand';
import { FocusStripWidget } from '../components/orka/dashboard/FocusStripWidget';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const API = '/api';

// Known field → friendly label mapping for task validation errors
const FIELD_LABELS = {
  task_priority: 'Priority',
  task_estimated_effort: 'Effort score',
  task_title: 'Title',
  task_status: 'Status',
  work_area_id: 'Work area',
  task_delivery_date: 'Delivery date',
  task_start_time: 'Start time',
  task_end_time: 'End time',
  task_estimated_minutes: 'Estimated minutes',
};

// Known field + error type → user-friendly message
const FRIENDLY_MESSAGES = {
  'task_priority:int_type': 'Priority is required.',
  'task_priority:missing': 'Priority is required.',
  'task_estimated_effort:int_type': 'Effort score is required.',
  'task_estimated_effort:missing': 'Effort score is required.',
  'task_title:string_type': 'Title is required.',
  'task_title:missing': 'Title is required.',
};

function parseTaskValidationError(error) {
  // Case 1: Pydantic structured validation errors (array of objects)
  if (Array.isArray(error?.detail) && error.detail.length > 0) {
    const messages = error.detail.map((err) => {
      const field = err.loc?.[err.loc.length - 1];
      const errType = err.type;
      const key = `${field}:${errType}`;

      // Check for a known friendly message
      if (FRIENDLY_MESSAGES[key]) return FRIENDLY_MESSAGES[key];

      // Fallback: use the field label + backend message
      const label = FIELD_LABELS[field] || field;
      return `${label}: ${err.msg}`;
    });
    return messages.join(' ');
  }

  // Case 2: String detail (e.g. "Cannot create org-scoped task: ...")
  if (typeof error?.detail === 'string') return error.detail;

  // Case 3: No useful info
  return 'Failed to save task';
}

const Dashboard = () => {
  const { user, logout, actingAs } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { orgId } = useOrg();
  const { workAreaName } = useWorkAreas();
  
  const [tasks, setTasks] = useState([]);
  const [backlogTasks, setBacklogTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [sheetsConfigured, setSheetsConfigured] = useState(true);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showEODModal, setShowEODModal] = useState(false);
  
  // Partial tasks state
  const [partialTasks, setPartialTasks] = useState([]);
  const [showPartialTasksModal, setShowPartialTasksModal] = useState(false);
  const [closingPartialTask, setClosingPartialTask] = useState(null);
  
  // EOD report state
  const [eodSummary, setEodSummary] = useState(null);
  const [showEODReport, setShowEODReport] = useState(false);
  
  // Done Report state
  const [showDoneReport, setShowDoneReport] = useState(false);
  const [showGetItDone, setShowGetItDone] = useState(false);
  const [getItDoneTask, setGetItDoneTask] = useState(null);
  const [doneReportTasks, setDoneReportTasks] = useState([]);
  const [doneReportLoading, setDoneReportLoading] = useState(false);
  const [doneReportStart, setDoneReportStart] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [doneReportEnd, setDoneReportEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [doneReportSort, setDoneReportSort] = useState('task_delivery_date');
  const [doneReportSortDir, setDoneReportSortDir] = useState('desc');
  
  // Default values for task modal (e.g. from timeline click)
  const [taskModalDefaults, setTaskModalDefaults] = useState(null);
  
  // Resizable pane state
  const [paneSplit, setPaneSplit] = useState(50);
  const [isResizing, setIsResizing] = useState(false);

  // Scheduling tab view (V3 widget-based dashboard)
  const [schedulingTab, setSchedulingTab] = useState(() => {
    try { return window.localStorage.getItem('orka.dashboard.schedulingTab') || 'planner'; }
    catch { return 'planner'; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('orka.dashboard.schedulingTab', schedulingTab); }
    catch { /* ignore */ }
  }, [schedulingTab]);
  
  // Staged changes for drag-and-drop save bar
  // Each change: { taskId, title, changes: { task_delivery_date, start_time, end_time }, originalTask }
  const [pendingChanges, setPendingChanges] = useState({});
  const [savingChanges, setSavingChanges] = useState(false);
  
  // Drag state
  const [draggingTask, setDraggingTask] = useState(null);

  // Reschedule prompt state
  const [reschedulePrompt, setReschedulePrompt] = useState(null); // { taskId, updates, resolve }

  // Multi-user view state: 'mine' | 'supporting' | 'both'
  const [viewMode, setViewMode] = useState('mine');
  const [supportingTasks, setSupportingTasks] = useState([]);
  const [supportingBacklogTasks, setSupportingBacklogTasks] = useState([]);
  const [supportedUsers, setSupportedUsers] = useState([]);
  const [observerOnly, setObserverOnly] = useState(false);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  // Fetch tasks for selected date (includes observed tasks)
  const fetchTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: dateStr, include_observed: 'true' });
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      
      const response = await fetch(`${API}/tasks?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Mark tasks where user is observer (not owner) for visual distinction
        // Phase 5E: check observer_membership_ids (canonical) alongside legacy observers
        const userMemId = user.current_membership_id;
        setTasks(data.map(t => ({
          ...t,
          _isObserved: (t.owner_membership_id ? t.owner_membership_id !== userMemId : t.owner_user_id !== user.user_id) && (
            (userMemId && (t.observer_membership_ids || []).includes(userMemId)) ||
            (t.observers || []).includes(user.user_id)
          ),
          _isSupporter: t.task_supporter_membership_id && (t.owner_membership_id ? t.owner_membership_id !== userMemId : t.owner_user_id !== user.user_id),
        })));
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [user, dateStr, actingAs, orgId]);

  // Fetch backlog tasks (no date assigned, includes observed)
  const fetchBacklogTasks = useCallback(async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({ backlog: 'true', include_observed: 'true' });
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      
      const response = await fetch(`${API}/tasks?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBacklogTasks(data);
      }
    } catch (error) {
      console.error('Failed to fetch backlog:', error);
    }
  }, [user, actingAs, orgId]);

  // Fetch supporting tasks (tasks of people I support via delegation)
  const fetchSupportingTasks = useCallback(async () => {
    if (!user || viewMode === 'mine') {
      setSupportingTasks([]);
      setSupportingBacklogTasks([]);
      return;
    }
    try {
      const params = new URLSearchParams({ date: dateStr });
      if (orgId) params.append('org_id', orgId);
      if (observerOnly) params.append('observer_only', 'true');
      const response = await fetch(`${API}/tasks/supporting?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSupportedUsers(data.supported_users || []);
        setSupportingTasks((data.tasks || []).map(t => ({ ...t, _isSupporting: true })));
      }
      // Also fetch backlog for supported users
      const bParams = new URLSearchParams({ backlog: 'true' });
      if (orgId) bParams.append('org_id', orgId);
      if (observerOnly) bParams.append('observer_only', 'true');
      const bResponse = await fetch(`${API}/tasks/supporting?${bParams}`, { credentials: 'include' });
      if (bResponse.ok) {
        const bData = await bResponse.json();
        setSupportingBacklogTasks((bData.tasks || []).map(t => ({ ...t, _isSupporting: true })));
      }
    } catch (error) {
      console.error('Failed to fetch supporting tasks:', error);
    }
  }, [user, viewMode, dateStr, observerOnly, orgId]);

  // Fetch calendar status
  const fetchCalendarStatus = useCallback(async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/oauth/calendar/status?${params}`, { credentials: 'include' });
      if (response.ok) setCalendarStatus(await response.json());
    } catch (error) {
      console.error('Failed to fetch calendar status:', error);
    }
  }, [user, actingAs]);

  // Fetch partial tasks (across all dates)
  const fetchPartialTasks = useCallback(async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks/partial?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPartialTasks(data);
      }
    } catch (error) {
      console.error('Failed to fetch partial tasks:', error);
    }
  }, [user, actingAs]);

  // Fetch EOD summary for selected date
  const fetchEODSummary = useCallback(async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({ date: dateStr });
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/eod-summaries?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEodSummary(data.length > 0 ? data[0] : null);
      }
    } catch (error) {
      console.error('Failed to fetch EOD summary:', error);
    }
  }, [user, dateStr, actingAs]);

  useEffect(() => {
    fetchTasks();
    fetchBacklogTasks();
    fetchCalendarStatus();
    fetchPartialTasks();
    fetchEODSummary();
    fetchSupportingTasks();
  }, [fetchTasks, fetchBacklogTasks, fetchCalendarStatus, fetchPartialTasks, fetchEODSummary, fetchSupportingTasks]);

  // Live refresh: poll tasks every 30s so supporter/observer visibility
  // updates automatically (e.g. when owner removes supporter assignment)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      fetchTasks();
      fetchSupportingTasks();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, fetchTasks, fetchSupportingTasks]);

  // Phase 11 (UX patch) — open the task edit modal when any chat message
  // dispatches `orka:open-task` (e.g. via a clickable Task ID link in the
  // FloatingChat reply). Looks up the task locally first; falls back to a
  // single-task fetch if it's not in the current view.
  useEffect(() => {
    const handler = async (ev) => {
      const taskId = ev?.detail?.task_id;
      if (!taskId) return;
      const inView = (tasks || []).find(t => t.task_id === taskId)
        || (backlogTasks || []).find(t => t.task_id === taskId)
        || (supportingTasks || []).find(t => t.task_id === taskId);
      if (inView) {
        setEditingTask(inView);
        setShowTaskModal(true);
        return;
      }
      try {
        const params = new URLSearchParams();
        if (actingAs) params.append('acting_as', actingAs);
        const res = await fetch(`${API}/tasks/${encodeURIComponent(taskId)}?${params}`,
          { credentials: 'include' });
        if (res.ok) {
          const t = await res.json();
          setEditingTask(t);
          setShowTaskModal(true);
        } else {
          toast.error('Task not found');
        }
      } catch {
        toast.error('Failed to open task');
      }
    };
    window.addEventListener('orka:open-task', handler);
    return () => window.removeEventListener('orka:open-task', handler);
  }, [tasks, backlogTasks, supportingTasks, actingAs]);

  // Sync calendar
  const syncCalendar = async () => {
    setSyncing(true);
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/oauth/calendar/sync?${params}`, { 
        method: 'POST', 
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days_back: 0, days_forward: 90 })
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Synced ${result.synced_count} new, ${result.updated_count} updated events`);
        fetchTasks();
        fetchBacklogTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Sync failed');
      }
    } catch (error) {
      toast.error('Calendar sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Sync just the selected day (CRUD both directions)
  const syncDay = async () => {
    setSyncing(true);
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/oauth/calendar/sync-day?${params}`, { 
        method: 'POST', 
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: dateStr })
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Day sync: ${result.synced_count} new, ${result.updated_count} updated, ${result.pushed_count || 0} pushed`);
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Day sync failed');
      }
    } catch (error) {
      toast.error('Day sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Auto-sync every 30 minutes
  useEffect(() => {
    if (!calendarStatus?.connected) return;
    
    const autoSync = async () => {
      try {
        const params = new URLSearchParams();
        if (actingAs) params.append('acting_as', actingAs);
        await fetch(`${API}/oauth/calendar/sync?${params}`, { 
          method: 'POST', 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ days_back: 0, days_forward: 7, silent: true })
        });
        fetchTasks();
        fetchBacklogTasks();
      } catch (error) {
        console.error('Auto-sync failed:', error);
      }
    };

    // Run auto-sync every 30 minutes
    const interval = setInterval(autoSync, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [calendarStatus?.connected, actingAs, fetchTasks, fetchBacklogTasks]);

  // Task operations
  const saveTask = async (taskData) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const url = editingTask ? `${API}/tasks/${editingTask.task_id}?${params}` : `${API}/tasks?${params}`;
      const response = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...taskData, task_delivery_date: taskData.task_local_date || dateStr })
      });
      if (response.ok) {
        toast.success(editingTask ? 'Task updated' : 'Task created');
        setShowTaskModal(false);
        setEditingTask(null);
        fetchTasks();
        fetchBacklogTasks();
        return true;
      } else {
        let errorMsg;
        try {
          const error = await response.json();
          errorMsg = parseTaskValidationError(error);
        } catch {
          errorMsg = `Save failed (HTTP ${response.status})`;
        }
        toast.error(errorMsg);
        return false;
      }
    } catch (error) {
      toast.error(error?.message || 'Network error — could not reach server');
      return false;
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks/${taskId}?${params}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) {
        toast.success('Task deleted');
        fetchTasks();
        fetchBacklogTasks();
      }
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Calculate A-Z sequencing based on task position (sorted by start_time)
  const calculateSequencing = useCallback((taskList) => {
    // Sort tasks by start_time
    const sorted = [...taskList].sort((a, b) => {
      if (!a.task_start_time && !b.task_start_time) return 0;
      if (!a.task_start_time) return 1;
      if (!b.task_start_time) return -1;
      return a.task_start_time.localeCompare(b.task_start_time);
    });
    
    // Assign A-Z based on position
    return sorted.map((task, index) => ({
      ...task,
      task_sequence: String.fromCharCode(65 + index) // A=65, B=66, etc.
    }));
  }, []);

  // Update task (including inline edits from TaskCard)
  const doUpdateTask = useCallback(async (taskId, updates) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const response = await fetch(`${API}/tasks/${taskId}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        // Handle date changes - auto-move task between views
        if (updates.task_delivery_date !== undefined) {
          const newDate = updates.task_delivery_date;
          const taskData = tasks.find(t => t.task_id === taskId) || backlogTasks.find(t => t.task_id === taskId);
          const mergedTask = taskData ? { ...taskData, ...updates } : null;
          
          if (!newDate || newDate === '') {
            // Moving to backlog
            setTasks(prev => prev.filter(t => t.task_id !== taskId));
            if (mergedTask) setBacklogTasks(prev => [...prev.filter(t => t.task_id !== taskId), mergedTask]);
            toast.info('Task moved to backlog');
          } else if (newDate !== dateStr) {
            // Moving to a different date - remove from current views
            setTasks(prev => prev.filter(t => t.task_id !== taskId));
            setBacklogTasks(prev => prev.filter(t => t.task_id !== taskId));
            toast.info(`Task moved to ${newDate}`);
          } else {
            // Same date or moving to current date
            setBacklogTasks(prev => prev.filter(t => t.task_id !== taskId));
            setTasks(prev => {
              const exists = prev.find(t => t.task_id === taskId);
              if (exists) return prev.map(t => t.task_id === taskId ? { ...t, ...updates } : t);
              if (mergedTask) return [...prev, mergedTask];
              return prev;
            });
          }
        } else {
          // No date change - update in place
          setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, ...updates } : t));
          setBacklogTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, ...updates } : t));
        }
        
        // If start_time or end_time changed, recalculate A-Z sequencing
        if (updates.task_start_time !== undefined || updates.task_end_time !== undefined) {
          // Build updated task list for sequencing
          const currentTasks = tasks.map(t => t.task_id === taskId ? { ...t, ...updates } : t);
          const tasksWithTime = currentTasks.filter(t => t.task_local_start_time);
          const sorted = [...tasksWithTime].sort((a, b) => {
            if (!a.task_start_time && !b.task_start_time) return 0;
            if (!a.task_start_time) return 1;
            if (!b.task_start_time) return -1;
            return a.task_start_time.localeCompare(b.task_start_time);
          });
          
          // Update sequencing for each task
          for (let i = 0; i < sorted.length; i++) {
            const newSeq = String.fromCharCode(65 + i); // A=65, B=66, etc.
            if (sorted[i].task_sequence !== newSeq) {
              const seqParams = new URLSearchParams();
              if (actingAs) seqParams.append('acting_as', actingAs);
              await fetch(`${API}/tasks/${sorted[i].task_id}?${seqParams}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ task_sequence: newSeq })
              });
            }
          }
          
          // Refresh tasks to show updated sequencing
          fetchTasks();
        }
        
        return true;
      }
      // Non-ok response — surface validation errors
      try {
        const error = await response.json();
        toast.error(parseTaskValidationError(error));
      } catch {
        toast.error(`Update failed (HTTP ${response.status})`);
      }
      return false;
    } catch {
      return false;
    }
  }, [actingAs, orgId, tasks, backlogTasks, dateStr, fetchTasks]);

  // Reschedule-aware update wrapper — prompts when date changes
  const updateTask = useCallback(async (taskId, updates) => {
    // If date is changing, check if this is a reschedule
    if (updates.task_delivery_date !== undefined) {
      const taskData = tasks.find(t => t.task_id === taskId) || backlogTasks.find(t => t.task_id === taskId);
      const oldDate = taskData?.task_delivery_date;
      const newDate = updates.task_delivery_date;
      // Only prompt if moving to a different date (not clearing to backlog, not same date)
      if (oldDate && newDate && oldDate !== newDate) {
        return new Promise((resolve) => {
          setReschedulePrompt({ taskId, updates, resolve });
        });
      }
    }
    return doUpdateTask(taskId, updates);
  }, [doUpdateTask, tasks, backlogTasks]);

  // Handle reschedule prompt response
  const handleRescheduleResponse = async (isReschedule) => {
    if (!reschedulePrompt) return;
    const { taskId, updates, resolve } = reschedulePrompt;
    if (isReschedule) {
      const taskData = tasks.find(t => t.task_id === taskId) || backlogTasks.find(t => t.task_id === taskId);
      const currentCount = taskData?.task_reschedule_count || 0;
      updates.task_reschedule_count = currentCount + 1;
    }
    setReschedulePrompt(null);
    const result = await doUpdateTask(taskId, updates);
    resolve(result);
  };

  // Save A-Z sequencing for all tasks in the day
  const saveSequencing = useCallback(async (taskList) => {
    const sequencedTasks = calculateSequencing(taskList.filter(t => t.task_local_start_time));
    
    // Update each task's sequencing
    for (const task of sequencedTasks) {
      if (task.task_sequence) {
        await updateTask(task.task_id, { task_sequence: task.task_sequence });
      }
    }
  }, [calculateSequencing, doUpdateTask]);

  const scheduleTask = async (taskId, startTime, endTime, targetDate = null) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks/schedule?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          task_id: taskId, 
          task_delivery_date: targetDate || dateStr, 
          task_start_time: startTime, 
          task_end_time: endTime 
        })
      });
      if (response.ok) {
        // After scheduling, recalculate and save A-Z sequencing
        const updatedTasks = await response.json();
        fetchTasks().then(() => {
          // Get fresh task list and update sequencing
          setTimeout(async () => {
            const params = new URLSearchParams({ date: targetDate || dateStr });
            if (actingAs) params.append('acting_as', actingAs);
            const res = await fetch(`${API}/tasks?${params}`, { credentials: 'include' });
            if (res.ok) {
              const freshTasks = await res.json();
              await saveSequencing(freshTasks);
              fetchTasks(); // Refresh to show updated sequencing
            }
          }, 100);
        });
        fetchBacklogTasks();
      }
      return response.ok;
    } catch {
      return false;
    }
  };

  const moveToBacklog = async (taskId) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks/${taskId}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ task_delivery_date: null, task_start_time: null, task_end_time: null })
      });
      if (response.ok) {
        toast.success('Task moved to backlog');
        fetchTasks();
        fetchBacklogTasks();
      }
    } catch {
      toast.error('Failed to move task');
    }
  };

  const moveToUnplanned = async (taskId) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks/${taskId}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ task_delivery_date: dateStr, task_start_time: null, task_end_time: null })
      });
      if (response.ok) {
        toast.success('Task moved to today');
        fetchTasks();
        fetchBacklogTasks();
      }
    } catch {
      toast.error('Failed to move task');
    }
  };

  // Stage a drag-drop change (doesn't save immediately)
  const stageDragDropChange = useCallback((task, changes) => {
    setPendingChanges(prev => ({
      ...prev,
      [task.task_id]: {
        taskId: task.task_id,
        task_title: task.task_title,
        isMeeting: task.task_is_meeting || task.is_calendar_import,
        changes,
        originalTask: task
      }
    }));

    // Update local state to show the change visually
    if (changes.task_delivery_date === dateStr && changes.task_start_time) {
      // Moving to planned for this day
      setTasks(prev => {
        // Remove from current position if exists
        const filtered = prev.filter(t => t.task_id !== task.task_id);
        // Add with new times
        return [...filtered, { ...task, ...changes }];
      });
      setBacklogTasks(prev => prev.filter(t => t.task_id !== task.task_id));
    } else if (changes.task_delivery_date === dateStr && !changes.task_start_time) {
      // Moving to unplanned
      setTasks(prev => {
        const existing = prev.find(t => t.task_id === task.task_id);
        if (existing) {
          return prev.map(t => t.task_id === task.task_id ? { ...t, ...changes } : t);
        }
        return [...prev, { ...task, ...changes }];
      });
      setBacklogTasks(prev => prev.filter(t => t.task_id !== task.task_id));
    } else if (!changes.task_delivery_date) {
      // Moving to backlog
      setTasks(prev => prev.filter(t => t.task_id !== task.task_id));
      setBacklogTasks(prev => {
        const existing = prev.find(t => t.task_id === task.task_id);
        if (existing) {
          return prev.map(t => t.task_id === task.task_id ? { ...t, ...changes } : t);
        }
        return [...prev, { ...task, ...changes }];
      });
    }
  }, [dateStr]);

  // Save all pending changes
  const saveAllChanges = async () => {
    const changes = Object.values(pendingChanges);
    if (changes.length === 0) return;

    setSavingChanges(true);
    let successCount = 0;
    let errorCount = 0;

    for (const change of changes) {
      try {
        const params = new URLSearchParams();
        if (actingAs) params.append('acting_as', actingAs);
        
        const response = await fetch(`${API}/tasks/${change.taskId}?${params}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(change.changes)
        });
        
        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch {
        errorCount++;
      }
    }

    setSavingChanges(false);
    setPendingChanges({});

    if (successCount > 0) {
      const meetingCount = changes.filter(c => c.isMeeting).length;
      let msg = `Saved ${successCount} change${successCount > 1 ? 's' : ''}`;
      if (meetingCount > 0) {
        msg += ` (${meetingCount} calendar event${meetingCount > 1 ? 's' : ''} updated, attendees notified)`;
      }
      toast.success(msg);
    }
    if (errorCount > 0) {
      toast.error(`Failed to save ${errorCount} change${errorCount > 1 ? 's' : ''}`);
    }

    // Refresh to get fresh data
    fetchTasks();
    fetchBacklogTasks();
  };

  // Cancel all pending changes
  const cancelAllChanges = () => {
    setPendingChanges({});
    fetchTasks();
    fetchBacklogTasks();
  };

  const pendingChangeCount = Object.keys(pendingChanges).length;

  // EOD functions
  const openEOD = async () => {
    setShowEODModal(true);
  };

  // Done Report
  const fetchDoneReport = async () => {
    setDoneReportLoading(true);
    try {
      const params = new URLSearchParams({ start_date: doneReportStart, end_date: doneReportEnd });
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const res = await fetch(`${API}/done-report?${params}`, { credentials: 'include' });
      if (res.ok) {
        setDoneReportTasks(await res.json());
      } else {
        toast.error('Failed to load report');
      }
    } catch { toast.error('Failed to load report'); }
    finally { setDoneReportLoading(false); }
  };

  const exportDoneReport = () => {
    if (!doneReportTasks.length) return;
    const headers = ['Date', 'Title', 'Status', 'Priority', 'Work Area', 'Effort', 'Actual Effort'];
    const rows = doneReportTasks.map(t => [
      t.task_local_date || '',
      `"${(t.task_title || '').replace(/"/g, '""')}"`,
      t.task_status || '',
      t.task_priority ?? '',
      workAreaName(t.work_area_id) || '',
      t.task_estimated_effort || '',
      t.task_actual_effort || t.task_actual_personal_effort || ''
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `done_report_${doneReportStart}_to_${doneReportEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${doneReportTasks.length} tasks`);
  };
  
  const handleEODComplete = () => {
    // Refresh tasks after EOD
    fetchTasks();
    fetchBacklogTasks();
    fetchPartialTasks();
    fetchEODSummary();
  };

  // Edit task
  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskModalDefaults(null);
    setShowTaskModal(true);
  };

  // Close a partial task (mark Done without effort capture)
  const closePartialTask = async (taskId) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks/${taskId}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'Done' })
      });
      if (response.ok) {
        toast.success('Partial task closed');
        fetchTasks();
        fetchPartialTasks();
        setClosingPartialTask(null);
      } else {
        toast.error('Failed to close task');
      }
    } catch {
      toast.error('Failed to close task');
    }
  };

  // Create task from timeline click or drag-to-create
  const handleCreateFromTimeline = (startTime, endTime, effort) => {
    setEditingTask(null);
    const defaults = { task_start_time: startTime, task_delivery_date: dateStr };
    if (endTime) defaults.task_end_time = endTime;
    if (effort) {
      defaults.task_estimated_effort = effort;
    } else if (endTime) {
      // Fallback: calculate effort from duration
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      const durationMins = (eh * 60 + em) - (sh * 60 + sm);
      if (durationMins <= 15) defaults.task_estimated_effort = 1;
      else if (durationMins <= 30) defaults.task_estimated_effort = 2;
      else if (durationMins <= 60) defaults.task_estimated_effort = 3;
      else if (durationMins <= 180) defaults.task_estimated_effort = 5;
      else if (durationMins <= 360) defaults.task_estimated_effort = 8;
      else defaults.task_estimated_effort = 13;
    }
    setTaskModalDefaults(defaults);
    setShowTaskModal(true);
  };

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const newSplit = ((e.clientY - rect.top) / rect.height) * 100;
    setPaneSplit(Math.min(Math.max(newSplit, 20), 80));
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Drag handlers
  const handleDragStart = (task) => {
    setDraggingTask(task);
  };

  const handleDragEnd = () => {
    setDraggingTask(null);
  };

  // Merge tasks based on viewMode
  const mergedTasks = useMemo(() => {
    if (viewMode === 'mine') return tasks;
    if (viewMode === 'supporting') return supportingTasks;
    // 'both' - combine mine + supporting
    return [...tasks, ...supportingTasks];
  }, [tasks, supportingTasks, viewMode]);

  const mergedBacklogTasks = useMemo(() => {
    if (viewMode === 'mine') return backlogTasks;
    if (viewMode === 'supporting') return supportingBacklogTasks;
    return [...backlogTasks, ...supportingBacklogTasks];
  }, [backlogTasks, supportingBacklogTasks, viewMode]);

  // Categorize tasks and calculate A-Z sequencing based on position
  const { plannedTasks, unplannedTasks } = useMemo(() => {
    const planned = mergedTasks.filter(t => t.task_local_start_time);
    const unplanned = mergedTasks.filter(t => !t.task_local_start_time);
    
    // Sort planned tasks by start_time and assign A-Z sequencing
    const sortedPlanned = [...planned].sort((a, b) => {
      return a.task_start_time.localeCompare(b.task_start_time);
    });
    
    // Assign A, B, C, etc. based on position (first task = A, second = B, etc.)
    const sequencedPlanned = sortedPlanned.map((task, index) => ({
      ...task,
      task_sequence: String.fromCharCode(65 + index) // A=65, B=66, C=67, etc.
    }));
    
    return { plannedTasks: sequencedPlanned, unplannedTasks: unplanned };
  }, [mergedTasks]);

  // Calculate stats
  const plannedEffort = plannedTasks.reduce((sum, t) => sum + (t.task_estimated_effort || 0), 0);
  const unplannedEffort = unplannedTasks.reduce((sum, t) => sum + (t.task_estimated_effort || 0), 0);
  const totalDayEffort = plannedEffort + unplannedEffort;
  const backlogEffort = mergedBacklogTasks.reduce((sum, t) => sum + (t.task_estimated_effort || 0), 0);
  
  const plannedDuration = plannedTasks.reduce((sum, t) => {
    if (t.task_local_start_time && t.task_local_end_time) {
      const [sh, sm] = t.task_local_start_time.split(':').map(Number);
      const [eh, em] = t.task_local_end_time.split(':').map(Number);
      return sum + (eh * 60 + em) - (sh * 60 + sm);
    }
    return sum;
  }, 0);

  // Time-based border state
  // Border indicates outstanding tasks to complete for EOD
  const getBorderState = useCallback(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const currentHour = new Date().getHours();
    const selected = format(selectedDate, 'yyyy-MM-dd');
    
    // Check if there are any incomplete tasks (not Done, not processed via EOD)
    // Partial tasks are considered "completed for the day" since effort was logged
    const incompleteTasks = tasks.filter(t => 
      ['To Do', 'In Progress', 'Blocked'].includes(t.task_status)
    );
    const hasIncomplete = incompleteTasks.length > 0;
    
    // Future dates - no indicator
    if (selected > today) {
      return { color: 'normal', hasIncomplete: false };
    }
    
    // Past dates - show red if there are still incomplete tasks
    if (selected < today) {
      return { 
        color: hasIncomplete ? 'red' : 'normal',
        hasIncomplete 
      };
    }
    
    // Today - show time-based color ONLY if there are incomplete tasks
    // If all tasks are done, no border indicator
    if (!hasIncomplete) {
      return { color: 'normal', hasIncomplete: false };
    }
    
    // There are incomplete tasks today - show time-based urgency
    if (currentHour < 13) return { color: 'green', hasIncomplete: true };
    if (currentHour <= 16) return { color: 'blue', hasIncomplete: true };
    if (currentHour <= 19) return { color: 'amber', hasIncomplete: true };
    return { color: 'red', hasIncomplete: true };
  }, [selectedDate, tasks]);

  const borderState = getBorderState();

  // Header actions for PageLayout
  const headerActions = (
    <div className="flex items-center gap-3">
      {/* Calendar Sync Day Button */}
      {calendarStatus?.connected && (
        <Button
          variant="ghost"
          className="h-9 px-3 rounded-full orka-bg-intent-soft indigo-900/20 orka-text-intent border border-indigo-100 dark:border-indigo-800 hover:orka-bg-intent-soft dark:hover:bg-indigo-900/30"
          onClick={syncDay}
          disabled={syncing}
          data-testid="sync-day"
          title="Sync this day with calendar (both directions)"
        >
          <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''} mr-1`} />
          <span className="text-xs font-medium">Sync Day</span>
        </Button>
      )}

      {/* Date Navigation */}
      <div className="orka-surface-subtle rounded-xl p-1 flex items-center">
        <Button 
          variant="ghost" 
          size="icon"
          className="h-8 w-8 rounded-lg"
          onClick={() => setSelectedDate(d => subDays(d, 1))}
          data-testid="prev-day"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          className="h-8 px-3 rounded-lg text-sm font-medium"
          onClick={() => setSelectedDate(new Date())}
          data-testid="go-today"
        >
          Today
        </Button>
        <Button 
          variant="ghost" 
          size="icon"
          className="h-8 w-8 rounded-lg"
          onClick={() => setSelectedDate(d => addDays(d, 1))}
          data-testid="next-day"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Date Picker with effort stats */}
      <DatePickerWithStats
        value={dateStr}
        onChange={(val) => val && setSelectedDate(parseISO(val))}
        triggerClassName="h-9 rounded-lg border orka-border-subtle bg-stone-50 dark:bg-slate-800 font-mono w-40"
      />

      {/* New Task */}
      <Button
        className="h-9 px-4 rounded-full bg-slate-900 slate-100 text-white dark:orka-text-primary shadow-sm hover:bg-slate-800 dark:hover:bg-slate-200"
        onClick={() => { setEditingTask(null); setTaskModalDefaults(null); setShowTaskModal(true); }}
        data-testid="add-task"
      >
        <Plus className="h-4 w-4 mr-1" />
        New Task
      </Button>
    </div>
  );

  return (
    <PageLayout 
      title={`Daily Dashboard • ${format(selectedDate, 'EEEE, MMMM d')}`}
      actions={headerActions}
      user={user} 
      onLogout={logout} 
      theme={theme} 
      setTheme={setTheme}
    >
      <div className="h-[calc(100vh-8rem)] flex flex-col">
        {/* Warning Banner */}
        {!sheetsConfigured && (
          <div className="orka-bg-warning-soft amber-900/20 border orka-border-subtle dark:border-amber-700 rounded-lg px-4 py-2 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 orka-text-warning" />
              <span className="text-sm text-amber-800">Google Sheets is not configured. Export functionality will be limited.</span>
            </div>
            <button 
              onClick={() => navigate('/settings')}
              className="text-sm font-medium orka-text-warning underline hover:text-amber-800"
            >
              Configure now →
            </button>
          </div>
        )}

        {/* V3 Today Command Band — unified headline + metric strip (PRD §8.6) */}
        <TodayCommandBand
          date={format(selectedDate, 'EEEE, MMMM d')}
          eyebrow="Daily command surface"
          title={isToday(selectedDate) ? 'Today' : format(selectedDate, 'EEEE')}
          subtitle={`${plannedTasks.length} planned · ${unplannedTasks.length} unplanned · ${mergedBacklogTasks.length} in backlog`}
          metrics={{
            effort: totalDayEffort,
            focusMinutes: plannedDuration,
            open: plannedTasks.filter(t => ['To Do', 'In Progress', 'Blocked'].includes(t.task_status)).length,
            risk: partialTasks.length + plannedTasks.filter(t => t.task_status === 'Blocked').length,
          }}
          leftSlot={
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex items-center orka-surface-subtle rounded-lg p-0.5 gap-0.5" data-testid="view-mode-toggle">
                <button
                  onClick={() => setViewMode('mine')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium orka-transition-colors ${viewMode === 'mine' ? 'orka-surface-base shadow-sm orka-text-primary' : 'orka-text-tertiary hover:orka-text-secondary'}`}
                  data-testid="view-mode-mine"
                >
                  Responsible
                </button>
                <button
                  onClick={() => setViewMode('supporting')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium orka-transition-colors ${viewMode === 'supporting' ? 'orka-surface-base shadow-sm orka-text-primary' : 'orka-text-tertiary hover:orka-text-secondary'}`}
                  data-testid="view-mode-supporting"
                >
                  <Users className="w-3.5 h-3.5 inline mr-1" />
                  Supporting
                </button>
                <button
                  onClick={() => setViewMode('both')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium orka-transition-colors ${viewMode === 'both' ? 'orka-surface-base shadow-sm orka-text-primary' : 'orka-text-tertiary hover:orka-text-secondary'}`}
                  data-testid="view-mode-both"
                >
                  Both
                </button>
              </div>

              {/* Observer Only filter */}
              {viewMode !== 'mine' && (
                <button
                  onClick={() => setObserverOnly(o => !o)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border orka-transition-colors ${observerOnly ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300' : 'orka-surface-base orka-border-subtle orka-text-tertiary hover:orka-text-secondary'}`}
                  data-testid="observer-only-toggle"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Observer Only
                </button>
              )}

              {/* Legends */}
              {viewMode !== 'mine' && supportedUsers.length > 0 && (
                <div className="flex items-center gap-2 text-xs orka-text-tertiary" data-testid="supporting-legend">
                  <span className="inline-block w-3 h-3 rounded-sm border-2 border-orange-400 bg-orange-50" />
                  <span>{supportedUsers.map(u => u.name || u.email).join(', ')}&rsquo;s tasks</span>
                </div>
              )}
              <div className="flex items-center gap-1 text-xs orka-text-tertiary" data-testid="observed-legend">
                <span className="inline-block w-3 h-3 rounded-sm border-2 border-teal-400 bg-teal-50" />
                <span>Observing</span>
              </div>

              {partialTasks.length > 0 && (
                <button
                  onClick={() => setShowPartialTasksModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full orka-bg-info-soft cyan-900/20 border orka-border-subtle dark:border-cyan-800 hover:orka-bg-info-soft dark:hover:bg-cyan-900/30 orka-transition-colors"
                  data-testid="partial-tasks-counter"
                >
                  <span className="w-5 h-5 rounded-full bg-cyan-500 text-white text-xs font-bold flex items-center justify-center">
                    {partialTasks.length}
                  </span>
                  <span className="orka-text-info font-medium text-xs">partially completed</span>
                </button>
              )}
              {eodSummary && (
                <button
                  onClick={() => setShowEODReport(true)}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full orka-bg-success-soft emerald-900/20 border orka-border-subtle hover:orka-bg-success-soft dark:hover:bg-emerald-900/30 orka-transition-colors"
                  data-testid="eod-report-link"
                >
                  <FileText className="w-3.5 h-3.5 orka-text-success" />
                  <span className="orka-text-success font-medium text-xs">EOD Report</span>
                </button>
              )}
            </div>
          }
        />

        <div className="h-4" />

        {/* V3 Focus Strip — compact widget row (Now · Recovery · Report) */}
        <div className="mb-4" data-testid="dashboard-v3-focus-strip">
          <FocusStripWidget
            focusTask={
              plannedTasks.find(t => t.task_status === 'In Progress') ||
              plannedTasks.find(t => ['To Do', 'Blocked'].includes(t.task_status) && (t.task_priority ?? 3) <= 1) ||
              plannedTasks[0] ||
              unplannedTasks[0] ||
              mergedBacklogTasks[0] ||
              null
            }
            counts={{
              partial: partialTasks.length,
              blocked: plannedTasks.filter(t => t.task_status === 'Blocked').length,
            }}
            partialTasks={partialTasks}
            eodSummary={eodSummary}
            partialCount={partialTasks.length}
            onStartFocus={(t) => { setGetItDoneTask(t); setShowGetItDone(true); }}
            onOpenTask={(t) => openEditTask(t)}
            onStartEOD={() => setShowEODModal(true)}
            onOpenEOD={() => setShowEODReport(true)}
            onOpenDoneReport={() => { setShowDoneReport(true); fetchDoneReport(); }}
            onOpenPartials={() => setShowPartialTasksModal(true)}
            onOpenBlocked={() => setSchedulingTab('planner')}
            onRecoverTask={(t) => t && openEditTask(t)}
          />
        </div>

        {/* Scheduling Tabs — Day Planner · Unscheduled · Backlog · Split */}
        <Tabs
          value={schedulingTab}
          onValueChange={setSchedulingTab}
          className="flex-1 min-h-0 flex flex-col"
          data-testid="scheduling-tabs"
        >
          <TabsList className="self-start mb-3 orka-surface-subtle" data-testid="scheduling-tabs-list">
            <TabsTrigger value="planner" data-testid="scheduling-tab-planner">
              <Clock className="w-3.5 h-3.5 mr-1.5" />
              Day Planner
              <span className="ml-1.5 text-[10px] orka-text-tertiary font-mono">{plannedTasks.length}</span>
            </TabsTrigger>
            <TabsTrigger value="unscheduled" data-testid="scheduling-tab-unscheduled">
              <ListTodo className="w-3.5 h-3.5 mr-1.5" />
              Unscheduled
              <span className="ml-1.5 text-[10px] orka-text-tertiary font-mono">{unplannedTasks.length}</span>
            </TabsTrigger>
            <TabsTrigger value="backlog" data-testid="scheduling-tab-backlog">
              <Inbox className="w-3.5 h-3.5 mr-1.5" />
              Backlog
              <span className="ml-1.5 text-[10px] orka-text-tertiary font-mono">{mergedBacklogTasks.length}</span>
            </TabsTrigger>
            <TabsTrigger value="split" data-testid="scheduling-tab-split">
              <GripHorizontal className="w-3.5 h-3.5 mr-1.5" />
              Split
            </TabsTrigger>
          </TabsList>

          {/* Planner tab — full-width Timeline */}
          <TabsContent value="planner" className="flex-1 min-h-0 mt-0 outline-none" data-testid="scheduling-tab-content-planner">
            <div className="h-full min-h-0">
              <Timeline
                tasks={plannedTasks}
                selectedDate={selectedDate}
                borderColor={borderState.color}
                totalEffort={totalDayEffort}
                totalDuration={plannedDuration}
                taskCount={plannedTasks.length}
                onTaskUpdate={updateTask}
                onTaskEdit={openEditTask}
                onTaskDelete={deleteTask}
                onCreateTask={handleCreateFromTimeline}
                onDragDropChange={(task, startTime, endTime) => {
                  stageDragDropChange(task, {
                    task_delivery_date: dateStr,
                    task_start_time: startTime,
                    task_end_time: endTime,
                  });
                }}
                draggingTask={draggingTask}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              />
            </div>
          </TabsContent>

          {/* Unscheduled tab — full-width ScheduledPane */}
          <TabsContent value="unscheduled" className="flex-1 min-h-0 mt-0 outline-none" data-testid="scheduling-tab-content-unscheduled">
            <div className="h-full min-h-0">
              <ScheduledPane
                tasks={unplannedTasks}
                borderColor={borderState.color}
                totalEffort={totalDayEffort}
                taskCount={unplannedTasks.length}
                onTaskUpdate={updateTask}
                onTaskEdit={openEditTask}
                onTaskDelete={deleteTask}
                onMoveToBacklog={(taskId) => {
                  const task = tasks.find(t => t.task_id === taskId) || backlogTasks.find(t => t.task_id === taskId);
                  if (task) {
                    stageDragDropChange(task, { task_delivery_date: null, task_start_time: null, task_end_time: null });
                  }
                }}
                draggingTask={draggingTask}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDropFromTimeline={(task) => {
                  stageDragDropChange(task, { task_delivery_date: dateStr, task_start_time: null, task_end_time: null });
                }}
              />
            </div>
          </TabsContent>

          {/* Backlog tab — full-width BacklogPane */}
          <TabsContent value="backlog" className="flex-1 min-h-0 mt-0 outline-none" data-testid="scheduling-tab-content-backlog">
            <div className="h-full min-h-0">
              <BacklogPane
                tasks={mergedBacklogTasks}
                totalEffort={backlogEffort}
                taskCount={mergedBacklogTasks.length}
                onTaskUpdate={updateTask}
                onTaskEdit={openEditTask}
                onTaskDelete={deleteTask}
                onMoveToToday={(taskId) => {
                  const task = mergedBacklogTasks.find(t => t.task_id === taskId) || mergedTasks.find(t => t.task_id === taskId);
                  if (task) {
                    stageDragDropChange(task, { task_delivery_date: dateStr, task_start_time: null, task_end_time: null });
                  }
                }}
                onMoveToBacklog={(task) => {
                  stageDragDropChange(task, { task_delivery_date: null, task_start_time: null, task_end_time: null });
                }}
                draggingTask={draggingTask}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              />
            </div>
          </TabsContent>

          {/* Split tab — legacy three-pane side-by-side view for cross-pane drag/drop */}
          <TabsContent value="split" className="flex-1 min-h-0 mt-0 outline-none" data-testid="scheduling-tab-content-split">
            <div className="flex gap-6 h-full min-h-0">
              {/* Left Column - Day Planner */}
              <div className="h-full min-h-0" style={{ width: '50%' }}>
                <Timeline
                  tasks={plannedTasks}
                  selectedDate={selectedDate}
                  borderColor={borderState.color}
                  totalEffort={totalDayEffort}
                  totalDuration={plannedDuration}
                  taskCount={plannedTasks.length}
                  onTaskUpdate={updateTask}
                  onTaskEdit={openEditTask}
                  onTaskDelete={deleteTask}
                  onCreateTask={handleCreateFromTimeline}
                  onDragDropChange={(task, startTime, endTime) => {
                    stageDragDropChange(task, {
                      task_delivery_date: dateStr,
                      task_start_time: startTime,
                      task_end_time: endTime,
                    });
                  }}
                  draggingTask={draggingTask}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                />
              </div>

              {/* Right Column - Unplanned + Backlog */}
              <div
                className="flex flex-col h-full min-h-0 overflow-hidden"
                style={{ width: '50%' }}
                onMouseMove={handleResizeMove}
                onMouseUp={handleResizeEnd}
                onMouseLeave={handleResizeEnd}
              >
                {/* Unplanned Pane */}
                <div style={{ height: `${paneSplit}%` }} className="min-h-0">
                  <ScheduledPane
                    tasks={unplannedTasks}
                    borderColor={borderState.color}
                    totalEffort={totalDayEffort}
                    taskCount={unplannedTasks.length}
                    onTaskUpdate={updateTask}
                    onTaskEdit={openEditTask}
                    onTaskDelete={deleteTask}
                    onMoveToBacklog={(taskId) => {
                      const task = tasks.find(t => t.task_id === taskId) || backlogTasks.find(t => t.task_id === taskId);
                      if (task) {
                        stageDragDropChange(task, { task_delivery_date: null, task_start_time: null, task_end_time: null });
                      }
                    }}
                    draggingTask={draggingTask}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDropFromTimeline={(task) => {
                      stageDragDropChange(task, { task_delivery_date: dateStr, task_start_time: null, task_end_time: null });
                    }}
                  />
                </div>

                {/* Resizer */}
                <div
                  className="h-3 flex items-center justify-center cursor-row-resize group hover:orka-surface-subtle dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                  onMouseDown={handleResizeStart}
                >
                  <div className="w-12 h-1 rounded-full bg-stone-300 slate-600 group-hover:bg-stone-400 transition-colors flex items-center justify-center">
                    <GripHorizontal className="w-4 h-4 orka-text-tertiary group-hover:orka-text-tertiary" />
                  </div>
                </div>

                {/* Backlog Pane */}
                <div style={{ height: `${100 - paneSplit}%` }} className="min-h-0">
                  <BacklogPane
                    tasks={mergedBacklogTasks}
                    totalEffort={backlogEffort}
                    taskCount={mergedBacklogTasks.length}
                    onTaskUpdate={updateTask}
                    onTaskEdit={openEditTask}
                    onTaskDelete={deleteTask}
                    onMoveToToday={(taskId) => {
                      const task = mergedBacklogTasks.find(t => t.task_id === taskId) || mergedTasks.find(t => t.task_id === taskId);
                      if (task) {
                        stageDragDropChange(task, { task_delivery_date: dateStr, task_start_time: null, task_end_time: null });
                      }
                    }}
                    onMoveToBacklog={(task) => {
                      stageDragDropChange(task, { task_delivery_date: null, task_start_time: null, task_end_time: null });
                    }}
                    draggingTask={draggingTask}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Floating Save Bar for Drag-Drop Changes */}
        {pendingChangeCount > 0 && (
          <div 
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 orka-surface-base orka-border-subtle border shadow-xl rounded-2xl px-6 py-4 flex items-center gap-6"
            data-testid="save-changes-bar"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full orka-bg-warning-soft flex items-center justify-center">
                <span className="text-lg font-bold orka-text-warning">{pendingChangeCount}</span>
              </div>
              <div>
                <p className="text-sm font-medium orka-text-primary">
                  Unsaved Change{pendingChangeCount > 1 ? 's' : ''}
                </p>
                <p className="text-xs orka-text-tertiary">
                  {Object.values(pendingChanges).filter(c => c.isMeeting).length > 0 && 
                    'Calendar events will be updated & attendees notified'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="rounded-full px-5 h-10 orka-border-default hover:orka-surface-subtle"
                onClick={cancelAllChanges}
                disabled={savingChanges}
                data-testid="cancel-all-changes"
              >
                Cancel
              </Button>
              <Button
                className="orka-bg-intent hover:bg-violet-700 text-white rounded-full px-5 h-10"
                onClick={saveAllChanges}
                disabled={savingChanges}
                data-testid="save-all-changes"
              >
                {savingChanges ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  `Save All Changes`
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Task Modal */}
      <TaskModal
        open={showTaskModal}
        onOpenChange={setShowTaskModal}
        task={editingTask}
        selectedDate={selectedDate}
        onSave={saveTask}
        defaultValues={taskModalDefaults}
      />

      {/* EOD Modal */}
      <EODModal
        open={showEODModal}
        onOpenChange={setShowEODModal}
        selectedDate={selectedDate}
        actingAs={actingAs}
        onComplete={handleEODComplete}
        onViewReport={eodSummary ? () => setShowEODReport(true) : null}
      />

      {/* Assistant Panel */}
      {showAssistant && (
        <AssistantPanel onClose={() => setShowAssistant(false)} />
      )}

      {/* Partial Tasks Modal */}
      <Dialog open={showPartialTasksModal} onOpenChange={setShowPartialTasksModal}>
        <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="partial-tasks-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 orka-text-info" />
              Partially Completed Tasks ({partialTasks.length})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {partialTasks.length === 0 ? (
              <p className="text-center orka-text-tertiary py-8">No partially completed tasks</p>
            ) : (
              partialTasks.map(task => (
                <div key={task.task_id} className="flex items-center justify-between p-3 rounded-lg border border orka-border-subtle hover:orka-surface-subtle dark:hover:bg-slate-800" data-testid={`partial-task-${task.task_id}`}>
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="font-medium orka-text-primary truncate">{task.task_title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs orka-text-tertiary">{task.task_local_date || 'No date'}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded orka-bg-info-soft cyan-900/30 orka-text-info">{task.work_area_id || 'General'}</span>
                      {task.task_actual_effort && <span className="text-xs orka-text-tertiary">Effort: {task.task_actual_effort}pts</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => { setShowPartialTasksModal(false); openEditTask(task); }}
                      data-testid={`view-partial-${task.task_id}`}
                    >
                      View
                    </Button>
                    {closingPartialTask === task.task_id ? (
                      <Loader2 className="h-4 w-4 animate-spin orka-text-success" />
                    ) : (
                      <Button
                        size="sm"
                        className="orka-bg-success hover:bg-emerald-700 text-xs"
                        onClick={() => { setClosingPartialTask(task.task_id); closePartialTask(task.task_id); }}
                        data-testid={`close-partial-${task.task_id}`}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Close
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartialTasksModal(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EOD Report Modal */}
      <Dialog open={showEODReport} onOpenChange={setShowEODReport}>
        <DialogContent className="sm:max-w-lg" data-testid="eod-report-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 orka-text-intent" />
              EOD Report — {format(selectedDate, 'MMM d, yyyy')}
            </DialogTitle>
          </DialogHeader>
          {eodSummary ? (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-4 gap-3">
                <div className="orka-bg-success-soft emerald-900/20 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold orka-text-success">{eodSummary.done_count || 0}</div>
                  <div className="text-[10px] orka-text-success font-medium">Completed</div>
                </div>
                <div className="orka-bg-info-soft cyan-900/20 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold orka-text-info">{eodSummary.partial_count || 0}</div>
                  <div className="text-[10px] orka-text-info font-medium">Partial</div>
                </div>
                <div className="orka-bg-warning-soft rounded-xl p-3 text-center">
                  <div className="text-xl font-bold orka-text-warning">{eodSummary.rescheduled_count || 0}</div>
                  <div className="text-[10px] orka-text-warning font-medium">Rescheduled</div>
                </div>
                <div className="bg-slate-100 slate-700 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold orka-text-secondary">{eodSummary.backlog_count || 0}</div>
                  <div className="text-[10px] orka-text-secondary font-medium">To Backlog</div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-3 flex items-center justify-between">
                <span className="text-sm font-medium orka-text-secondary">Total Effort Logged</span>
                <span className="text-lg font-bold orka-text-intent">{eodSummary.total_effort || 0} points</span>
              </div>

              {eodSummary.outcomes && eodSummary.outcomes.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="orka-surface-subtle">
                      <tr>
                        <th className="text-left p-2 font-semibold orka-text-secondary text-xs">Task</th>
                        <th className="text-left p-2 font-semibold orka-text-secondary text-xs">Action</th>
                        <th className="text-left p-2 font-semibold orka-text-secondary text-xs">Effort</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {eodSummary.outcomes.map((o, i) => (
                        <tr key={i} className="hover:orka-surface-subtle dark:hover:bg-slate-800">
                          <td className="p-2 text-xs orka-text-primary truncate max-w-[200px]">{o.title}</td>
                          <td className="p-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              o.action === 'done' ? 'bg-emerald-100 text-emerald-700' :
                              o.action === 'partial' ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' :
                              o.action === 'reschedule' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 dark:bg-slate-700 orka-text-secondary'
                            }`}>
                              {o.action === 'done' ? 'Done' : o.action === 'partial' ? 'Partial' : o.action === 'reschedule' ? 'Rescheduled' : 'Backlog'}
                            </span>
                          </td>
                          <td className="p-2 text-xs orka-text-secondary">{o.effort_recorded ? `${o.effort_recorded}pts` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="text-[10px] orka-text-tertiary text-right">
                Completed at {eodSummary.completed_at ? new Date(eodSummary.completed_at).toLocaleTimeString() : 'N/A'}
              </p>
            </div>
          ) : (
            <p className="text-center orka-text-tertiary py-8">No EOD report for this date</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEODReport(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Prompt Dialog */}
      <Dialog open={!!reschedulePrompt} onOpenChange={(open) => { if (!open) { setReschedulePrompt(null); reschedulePrompt?.resolve(false); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="reschedule-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 orka-text-warning" />
              Is this a reschedule?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm orka-text-secondary">
            You're moving this task to a different date. Should this count as a reschedule?
          </p>
          <p className="text-xs orka-text-tertiary flex items-center gap-1">
            Reschedule count helps you track how often tasks get pushed. It does not affect the task.
          </p>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => handleRescheduleResponse(false)} data-testid="reschedule-no">
              No, just moving
            </Button>
            <Button className="orka-bg-warning hover:orka-bg-warning text-white" onClick={() => handleRescheduleResponse(true)} data-testid="reschedule-yes">
              Yes, reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Done Report Modal */}
      <Dialog open={showDoneReport} onOpenChange={setShowDoneReport}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="done-report-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 orka-text-success" />
              Done Report
            </DialogTitle>
          </DialogHeader>
          
          {/* Date Range Selector */}
          <div className="flex items-center gap-3 py-2 border-b border orka-border-subtle">
            <label className="text-xs orka-text-tertiary">From</label>
            <input type="date" value={doneReportStart} onChange={(e) => setDoneReportStart(e.target.value)} className="text-sm border rounded px-2 py-1" />
            <label className="text-xs orka-text-tertiary">To</label>
            <input type="date" value={doneReportEnd} onChange={(e) => setDoneReportEnd(e.target.value)} className="text-sm border rounded px-2 py-1" />
            <Button size="sm" onClick={fetchDoneReport} disabled={doneReportLoading} data-testid="done-report-run">
              {doneReportLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Run
            </Button>
            <div className="flex-1" />
            <Badge variant="secondary">{doneReportTasks.length} tasks</Badge>
            <Button size="sm" variant="outline" onClick={exportDoneReport} disabled={!doneReportTasks.length} data-testid="done-report-export">
              Export CSV
            </Button>
          </div>

          {/* Results Table */}
          <div className="flex-1 overflow-auto">
            {doneReportLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin orka-text-tertiary" /></div>
            ) : doneReportTasks.length === 0 ? (
              <div className="text-center py-12 orka-text-tertiary text-sm">No done/partial tasks in this range</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="orka-surface-subtle sticky top-0">
                  <tr>
                    {[
                      { key: 'task_delivery_date', label: 'Date' },
                      { key: 'task_title', label: 'Title' },
                      { key: 'task_status', label: 'Status' },
                      { key: 'task_priority', label: 'P' },
                      { key: 'work_area_id', label: 'Area' },
                      { key: 'task_estimated_effort', label: 'Est.' },
                      { key: 'task_actual_effort', label: 'Actual' },
                    ].map(col => (
                      <th key={col.key}
                        className="text-left p-2 font-semibold orka-text-secondary text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 slate-700"
                        onClick={() => {
                          if (doneReportSort === col.key) setDoneReportSortDir(d => d === 'asc' ? 'desc' : 'asc');
                          else { setDoneReportSort(col.key); setDoneReportSortDir('desc'); }
                        }}
                      >
                        {col.label} {doneReportSort === col.key ? (doneReportSortDir === 'asc' ? '↑' : '↓') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[...doneReportTasks].sort((a, b) => {
                    const av = a[doneReportSort] ?? '';
                    const bv = b[doneReportSort] ?? '';
                    if (av < bv) return doneReportSortDir === 'asc' ? -1 : 1;
                    if (av > bv) return doneReportSortDir === 'asc' ? 1 : -1;
                    return 0;
                  }).map(t => (
                    <tr key={t.task_id} className="hover:orka-surface-subtle dark:hover:bg-slate-800">
                      <td className="p-2 text-xs orka-text-secondary whitespace-nowrap">{t.task_local_date || '-'}</td>
                      <td className="p-2 text-xs orka-text-primary max-w-[250px] truncate">{t.task_title}</td>
                      <td className="p-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          t.task_status === 'Done' ? 'bg-emerald-100 text-emerald-700' : 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                        }`}>{t.task_status}</span>
                      </td>
                      <td className="p-2 text-xs text-center font-mono">{t.task_priority ?? '-'}</td>
                      <td className="p-2 text-xs orka-text-tertiary truncate max-w-[100px]">{workAreaName(t.work_area_id) || '-'}</td>
                      <td className="p-2 text-xs text-center font-mono">{t.task_estimated_effort || '-'}</td>
                      <td className="p-2 text-xs text-center font-mono">{t.task_actual_effort || t.task_actual_personal_effort || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default Dashboard;
